import { API_BASE, SESSION_KEY, VOICE_API_BASE } from './config'

export class ApiError extends Error {
  status: number
  data?: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

function getToken(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY)
  } catch {
    return null
  }
}

async function requestJson<T>(url: string, opts: RequestInit, requiresAuth: boolean): Promise<T> {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 20_000)
  const externalSignal = opts.signal
  const abortFromCaller = () => controller.abort()
  externalSignal?.addEventListener('abort', abortFromCaller, { once: true })

  try {
    const res = await fetch(url, { ...opts, signal: controller.signal })
    const text = await res.text()
    let data: unknown = {}
    if (text) {
      try { data = JSON.parse(text) } catch { data = { error: text.slice(0, 300) } }
    }

    if (!res.ok) {
      if (requiresAuth && res.status === 401) {
        window.dispatchEvent(new CustomEvent('clyr-admin-auth-required'))
      }
      throw new ApiError(
        (data as { error?: string }).error || `Request failed (${res.status})`,
        res.status,
        data,
      )
    }
    return data as T
  } catch (error) {
    if (error instanceof ApiError) throw error
    if (controller.signal.aborted && !externalSignal?.aborted) {
      throw new ApiError('The request timed out. Check the service and try again.', 0)
    }
    throw new ApiError(error instanceof Error ? error.message : 'Network request failed', 0)
  } finally {
    window.clearTimeout(timeout)
    externalSignal?.removeEventListener('abort', abortFromCaller)
  }
}

export async function api<T = unknown>(
  path: string,
  opts: RequestInit & { skipAuth?: boolean } = {},
): Promise<T> {
  const { skipAuth, headers: customHeaders, ...rest } = opts
  const headers = new Headers(customHeaders)
  headers.set('Accept', 'application/json')

  if (rest.body && !headers.has('Content-Type') && !(rest.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const url = `${API_BASE}${path}`
  if (!skipAuth) {
    const token = getToken()
    if (!token) throw new ApiError('Not authenticated', 401)
    headers.set('Authorization', `Bearer ${token}`)
  }

  return requestJson<T>(url, { ...rest, headers }, !skipAuth)
}

export async function voiceApi<T = unknown>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers = new Headers(opts.headers)
  headers.set('Accept', 'application/json')
  const token = getToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  return requestJson<T>(`${VOICE_API_BASE}${path}`, { ...opts, headers }, false)
}

export function voiceApiUrl(path: string): string {
  const token = getToken()
  const sep = path.includes('?') ? '&' : '?'
  return `${VOICE_API_BASE}${path}${token ? `${sep}token=${encodeURIComponent(token)}` : ''}`
}

export function apiUrl(path: string): string {
  const token = getToken()
  const sep = path.includes('?') ? '&' : '?'
  return `${API_BASE}${path}${token ? `${sep}token=${encodeURIComponent(token)}` : ''}`
}
